import { Episode } from "@/types/episode";

interface AudioEngineEvents {
  onTimeUpdate: (time: number) => void;
  onDurationChange: (duration: number) => void;
  onError: (error: Error) => void;
  onEnded: () => void;
  onStateChange: (state: AudioEngineState) => void;
}

type AudioEngineState = "idle" | "loading" | "playing" | "paused" | "error";

export class AudioEngine {
  private audio: HTMLAudioElement;
  private events: AudioEngineEvents;
  private state: AudioEngineState = "idle";

  constructor(events: AudioEngineEvents) {
    this.audio = new Audio();
    this.audio.preload = "auto";
    this.events = events;
    this.setupEventListeners();
  }

  private setupEventListeners() {
    this.audio.addEventListener("timeupdate", () => {
      this.events.onTimeUpdate(this.audio.currentTime);
    });

    this.audio.addEventListener("durationchange", () => {
      if (this.audio.duration && !isNaN(this.audio.duration)) {
        this.events.onDurationChange(this.audio.duration);
      }
    });

    this.audio.addEventListener("playing", () => {
      this.setState("playing");
    });

    this.audio.addEventListener("pause", () => {
      this.setState("paused");
    });

    this.audio.addEventListener("ended", () => {
      this.events.onEnded();
      this.setState("idle");
    });

    this.audio.addEventListener("error", () => {
      const error = this.audio.error;
      const message = error
        ? `Audio error: ${error.code} - ${error.message}`
        : "Unknown audio error";
      this.events.onError(new Error(message));
      this.setState("error");
    });
  }

  private setState(newState: AudioEngineState) {
    this.state = newState;
    this.events.onStateChange(newState);
  }

  private getProxiedUrl(url: string): string {
    // If URL is already proxied, return as is
    if (url.startsWith("https://api.tempolabs.ai/proxy-asset")) {
      return url;
    }

    // If URL is already HTTPS, return as is
    if (url.startsWith("https://")) {
      return url;
    }

    // Otherwise, proxy the URL
    return `https://api.tempolabs.ai/proxy-asset?url=${encodeURIComponent(url)}`;
  }

  async loadAndPlay(episode: Episode) {
    try {
      this.audio.pause();
      this.audio.currentTime = 0;
      this.setState("loading");

      if (!episode.audioUrl) {
        throw new Error("No audio URL provided");
      }

      // Get proxied URL
      const audioUrl = this.getProxiedUrl(episode.audioUrl);
      console.log("Loading audio from URL:", audioUrl);

      // Set source and load
      this.audio.src = audioUrl;
      await this.audio.load();

      // Attempt to play
      await this.audio.play();
      this.setState("playing");
    } catch (error) {
      console.error("Failed to load and play:", error);
      this.events.onError(
        error instanceof Error ? error : new Error(String(error)),
      );
      this.setState("error");
      throw error;
    }
  }

  async play() {
    if (!this.audio.src) {
      throw new Error("No audio source set");
    }

    try {
      await this.audio.play();
      this.setState("playing");
    } catch (error) {
      console.error("Play error:", error);
      this.events.onError(
        error instanceof Error ? error : new Error(String(error)),
      );
      this.setState("error");
      throw error;
    }
  }

  pause() {
    this.audio.pause();
    this.setState("paused");
  }

  seek(time: number) {
    if (this.audio.readyState > 0) {
      this.audio.currentTime = time;
    }
  }

  setVolume(volume: number) {
    this.audio.volume = volume;
  }

  cleanup() {
    this.pause();
    this.audio.src = "";
  }

  getState(): AudioEngineState {
    return this.state;
  }
}
